import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'

const initialState = {
  coupons: null,
  pagination: null,
  getCoupons: {
    status: null,
    error: null,
  },
  createCoupon: {
    status: null,
    error: null,
  },
  setActiveStatus: {
    status: null,
    error: null,
  },
  setShowInPromoStatus: {
    status: null,
    error: null,
  },
  delistCoupon: {
    status: null,
    error: null,
  },
}

export const getCoupons = createAsyncThunk(
  'coupon/getCoupons',
  async (
    {
      page = 1,
      limit = 6,
      couponNumber,
      couponCode,
      couponTarget,
      couponType,
      active,
      branchCode,
      sku,
      itemCategory,
      sortBy,
      sortDirection,
    },
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(couponNumber && { couponNumber }),
        ...(couponCode && { couponCode }),
        ...(couponTarget && { couponTarget }),
        ...(couponType && { couponType }),
        ...(active && { active }),
        ...(branchCode && { branchCode }),
        ...(sku && { sku }),
        ...(itemCategory && { itemCategory }),
        ...(sortBy && { sortBy }),
        ...(sortDirection && { sortDirection }),
      })

      const response = await client.get(`/coupons?${queryParams.toString()}`)
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createCoupon = createAsyncThunk(
  'coupon/createCoupon',
  async (payload, { rejectWithValue }) => {
    try {
      console.log('Coupon payload', payload)
      const response = await client.post('/coupons', payload)
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const setActiveStatus = createAsyncThunk(
  'coupon/setActiveStatus',
  async ({ couponNumber, active }, { rejectWithValue }) => {
    try {
      const response = await client.post(`/coupons/${couponNumber}/set-active`, { active })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const setShowInPromoStatus = createAsyncThunk(
  'coupon/setShowInPromoStatus',
  async ({ couponNumber, showInPromo }, { rejectWithValue }) => {
    try {
      const response = await client.post(`/coupons/${couponNumber}/set-show-in-promo`, {
        showInPromo,
      })
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const delistCoupon = createAsyncThunk(
  'coupon/delistCoupon',
  async (couponNumber, { rejectWithValue }) => {
    try {
      const response = await client.post(`/coupons/${couponNumber}/delist`)
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

const couponSlice = createSlice({
  name: 'coupon',
  initialState,
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getCoupons
      .addCase(getCoupons.pending, (state) => {
        state.getCoupons.status = 'loading'
        state.getCoupons.error = null
        state.setActiveStatus.error = null
        state.setShowInPromoStatus.error = null
        state.delistCoupon.error = null
      })
      .addCase(getCoupons.fulfilled, (state, action) => {
        state.getCoupons.status = 'succeeded'
        state.getCoupons.error = null
        state.setActiveStatus.error = null
        state.setShowInPromoStatus.error = null
        state.delistCoupon.error = null

        if (action.payload.data.success) {
          state.coupons = action.payload.data.data
          state.pagination = action.payload.data.pagination
        }
      })
      .addCase(getCoupons.rejected, (state, action) => {
        state.getCoupons.status = 'failed'
        state.getCoupons.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
        state.coupons = null
      })
      // createCoupon
      .addCase(createCoupon.pending, (state) => {
        state.createCoupon.status = 'loading'
        state.createCoupon.error = null
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.createCoupon.status = 'succeeded'
        state.createCoupon.error = null

        if (action.payload.data.success) {
          state.coupons = state.coupons || []
          state.coupons.unshift(action.payload.data.data)
        }
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.createCoupon.status = 'failed'
        state.createCoupon.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // setActiveStatus
      .addCase(setActiveStatus.pending, (state) => {
        state.setActiveStatus.status = 'loading'
      })
      .addCase(setActiveStatus.fulfilled, (state, action) => {
        state.setActiveStatus.status = 'succeeded'
        state.setActiveStatus.error = null

        if (action.payload.data.success) {
          const couponIndex = state.coupons.findIndex(
            (coupon) => coupon.couponNumber === action.payload.data.data.couponNumber,
          )

          if (couponIndex !== -1) {
            state.coupons[couponIndex].active = action.payload.data.data.active
          }
        }
      })
      .addCase(setActiveStatus.rejected, (state, action) => {
        state.setActiveStatus.status = 'failed'
        state.setActiveStatus.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      .addCase(setShowInPromoStatus.pending, (state) => {
        state.setShowInPromoStatus.status = 'loading'
      })
      .addCase(setShowInPromoStatus.fulfilled, (state, action) => {
        state.setShowInPromoStatus.status = 'succeeded'
        state.setShowInPromoStatus.error = null
        if (action.payload.data.success) {
          const couponIndex = state.coupons.findIndex(
            (coupon) => coupon.couponNumber === action.payload.data.data.couponNumber,
          )
          if (couponIndex !== -1) {
            state.coupons[couponIndex].showInPromo = action.payload.data.data.showInPromo
          }
        }
      })
      .addCase(setShowInPromoStatus.rejected, (state, action) => {
        state.setShowInPromoStatus.status = 'failed'
        state.setShowInPromoStatus.error = {
          status: action.payload?.status,
          message: action.payload?.message,
        }
      })
      // delistCoupon
      .addCase(delistCoupon.pending, (state) => {
        state.delistCoupon.status = 'loading'
      })
      .addCase(delistCoupon.fulfilled, (state, action) => {
        state.delistCoupon.status = 'succeeded'
        state.delistCoupon.error = null

        if (action.payload.data.success) {
          const couponIndex = state.coupons.findIndex(
            (coupon) => coupon.couponNumber === action.payload.data.data.couponNumber,
          )

          if (couponIndex !== -1) {
            const delistedCoupon = { ...state.coupons[couponIndex], ...action.payload.data.data }
            const updatedCoupons = [...state.coupons]
            updatedCoupons[couponIndex] = delistedCoupon
            state.coupons = updatedCoupons
          }
        }
      })
      .addCase(delistCoupon.rejected, (state, action) => {
        state.delistCoupon.status = 'failed'
        state.delistCoupon.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})

// Selectors
export const selectCoupons = (state) => state.coupon.coupons
export const selectGetCouponsStatus = (state) => state.coupon.getCoupons.status
export const selectGetCouponsError = (state) => state.coupon.getCoupons.error
export const selectCreateCouponStatus = (state) => state.coupon.createCoupon.status
export const selectCreateCouponError = (state) => state.coupon.createCoupon.error
export const selectSetActiveStatusStatus = (state) => state.coupon.setActiveStatus.status
export const selectSetActiveStatusError = (state) => state.coupon.setActiveStatus.error
export const selectSetShowInPromoStatusStatus = (state) => state.coupon.setShowInPromoStatus.status
export const selectSetShowInPromoStatusError = (state) => state.coupon.setShowInPromoStatus.error
export const selectDelistCouponStatus = (state) => state.coupon.delistCoupon.status
export const selectDelistCouponError = (state) => state.coupon.delistCoupon.error
export const selectPagination = (state) => state.coupon.pagination

export const { clearStatus } = couponSlice.actions
export default couponSlice.reducer
